import { useState } from 'react'
import { Link } from 'react-router-dom'

const WA_URL = `https://wa.me/919418040013?text=${encodeURIComponent('Hi, I would like to enquire about a stay at Scenic Solitude.')}`
const MAPS_URL = 'https://maps.app.goo.gl/dtcNRhkxs7jkZFGM7'

// WhatsApp — round badge, official green
function WhatsAppIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#25D366" />
      <path fill="#fff" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

// Instagram — round badge, official gradient
function InstagramIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <radialGradient id="ig-f" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="12" fill="url(#ig-f)" />
      <path fill="#fff" fillRule="evenodd" clipRule="evenodd" d="M15 5.5H9A3.5 3.5 0 0 0 5.5 9v6A3.5 3.5 0 0 0 9 18.5h6a3.5 3.5 0 0 0 3.5-3.5V9A3.5 3.5 0 0 0 15 5.5zm2.25 9.5A2.25 2.25 0 0 1 15 17.25H9A2.25 2.25 0 0 1 6.75 15V9A2.25 2.25 0 0 1 9 6.75h6A2.25 2.25 0 0 1 17.25 9v6z" />
      <path fill="#fff" d="M12 8.75a3.25 3.25 0 1 0 0 6.5 3.25 3.25 0 0 0 0-6.5zm0 5.25a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
      <circle fill="#fff" cx="15.5" cy="8.5" r="0.75" />
    </svg>
  )
}

// Warm amber — matches transparent header gradient base
const BG = '#f8f5efff'
const colLabel = 'text-[10px] tracking-[0.3em] uppercase font-semibold mb-4 block text-ink'
const colLink = 'text-sm text-ink hover:text-ink-soft transition-colors duration-200'

function MobileAccordion({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid rgba(26,26,22,0.10)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span className="text-sm font-semibold" style={{ color: '#1a1a16' }}>{title}</span>
        <span className="text-lg leading-none" style={{ color: 'rgba(26,26,22,0.45)' }}>{open ? '−' : '+'}</span>
      </button>
      <div style={{ overflow: 'hidden', maxHeight: open ? '240px' : '0', transition: 'max-height 0.3s ease', paddingBottom: open ? '16px' : '0' }}>
        <ul className="flex flex-col gap-3.5">
          {children}
        </ul>
      </div>
    </div>
  )
}

export function Footer() {
  return (
    <footer style={{ backgroundColor: BG }}>
      <div className="mx-auto max-w-7xl px-8 lg:px-16 pt-10 pb-6">

        {/* Mobile layout — accordion */}
        <div className="lg:hidden mb-6">
          {/* Brand */}
          <div className="flex items-center gap-3 mb-4 pb-5 border-b border-ink/8">
            <img src="/images/logo3.jpeg" alt="Scenic Solitude" className="h-9 w-9 object-contain rounded" />
            <div>
              <p className="text-sm font-semibold tracking-wide text-ink leading-tight">Scenic Solitude</p>
              <p className="text-[9px] tracking-[0.3em] uppercase text-ink-soft mt-0.5">Himalayas</p>
            </div>
          </div>

          <MobileAccordion title="Explore">
            <li><Link to="/property-rules" className={colLink}>Property Rules</Link></li>
            <li><a href="#" className={colLink}>Terms &amp; Conditions</a></li>
            <li><Link to="/cancellation-policy" className={colLink}>Cancellation Policy</Link></li>
          </MobileAccordion>

          <MobileAccordion title="Contact">
            <li><Link to="/contact" className={colLink}>Get in Touch</Link></li>
            <li><a href={WA_URL} target="_blank" rel="noopener noreferrer" className={colLink}>WhatsApp</a></li>
            <li><a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className={colLink}>View on Maps ↗</a></li>
          </MobileAccordion>

          <MobileAccordion title="Quick Links">
            <li><Link to="/inquire" className={colLink}>Book a Stay</Link></li>
            <li><Link to="/gallery" className={colLink}>Gallery</Link></li>
            <li><Link to="/about" className={colLink}>About Us</Link></li>
          </MobileAccordion>
        </div>

        {/* Desktop layout — original 4-col grid */}
        <div className="hidden lg:grid gap-8 grid-cols-[1.8fr_1fr_1fr_1fr]">
          <div className="flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <img src="/images/logo3.jpeg" alt="Scenic Solitude" className="h-12 w-12 object-contain rounded" />
              <div>
                <p className="text-base font-semibold tracking-wide text-ink leading-tight">Scenic Solitude</p>
                <p className="text-[9px] tracking-[0.35em] uppercase text-ink-soft mt-0.5">Himalayas</p>
              </div>
            </div>
            <p className="text-sm text-ink leading-relaxed max-w-[220px] mb-5">
              Treehouses &amp; Wooden Village at Sojha.<br />Kullu District, Himachal Pradesh.
            </p>
            <a href="tel:+919418040013" className="flex items-center gap-2 text-sm text-ink hover:text-ink-soft transition-colors duration-200">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.95 11 19.79 19.79 0 01.88 2.38 2 2 0 012.86.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.06 6.06l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              +91 94180 40013
            </a>
          </div>
          <div>
            <span className={colLabel}>Explore</span>
            <ul className="flex flex-col gap-3.5">
              <li><Link to="/property-rules" className={colLink}>Property Rules</Link></li>
              <li><a href="#" className={colLink}>Terms &amp; Conditions</a></li>
              <li><Link to="/cancellation-policy" className={colLink}>Cancellation Policy</Link></li>
            </ul>
          </div>
          <div>
            <span className={colLabel}>Contact</span>
            <ul className="flex flex-col gap-3.5">
              <li><Link to="/contact" className={colLink}>Get in Touch</Link></li>
              <li><a href={WA_URL} target="_blank" rel="noopener noreferrer" className={colLink}>WhatsApp</a></li>
              <li><a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className={colLink}>View on Maps ↗</a></li>
            </ul>
          </div>
          <div>
            <span className={colLabel}>Quick Links</span>
            <ul className="flex flex-col gap-3.5">
              <li><Link to="/inquire" className={colLink}>Book a Stay</Link></li>
              <li><Link to="/gallery" className={colLink}>Gallery</Link></li>
              <li><Link to="/about" className={colLink}>About Us</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-8 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: '1px solid rgba(26,26,22,0.12)' }}>
          <p className="text-[11px] tracking-[0.2em] uppercase text-ink">
            © {new Date().getFullYear()} Scenic Solitude Himalayas
          </p>
          <div className="flex items-center gap-3">
            <span className="text-[10px] tracking-[0.25em] uppercase text-ink font-semibold mr-1">Follow Us</span>
            <a href="https://www.instagram.com/scenicsolitudehimalayas" target="_blank" rel="noopener noreferrer"
              aria-label="Instagram" className="opacity-75 hover:opacity-100 transition-opacity duration-200">
              <InstagramIcon />
            </a>
            <a href={WA_URL} target="_blank" rel="noopener noreferrer"
              aria-label="WhatsApp" className="opacity-75 hover:opacity-100 transition-opacity duration-200">
              <WhatsAppIcon />
            </a>
          </div>
        </div>

      </div>
    </footer>
  )
}
